// src/lib/artifacts.js
// ───────────────────────────────────────────────────────────────────────────
// 📁 Archivos v2 — artefactos de prepress versionados (PLAN-ARCHIVOS-PREPRESS, Fase 1)
// Cada "artefacto" es una ranura lógica de archivo (de uno de los 7 tipos) con un
// HISTORIAL append-only de versiones y una aprobación con traza (quién/cuándo/motivo).
// Modelo:
//   jobs/{jobId}/artifacts/{artifactId}            metadatos + currentVersion + approval
//   jobs/{jobId}/artifacts/{artifactId}/versions   versiones (append-only)
// El nombre lo ESTANDARIZA el sistema (deriveStandardName), no el operador.
// ───────────────────────────────────────────────────────────────────────────
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";

// Taxonomía unificada de archivos: artefactos de PREPRESS (7, estilo Esko) +
// DOCUMENTOS de apoyo (manuales de construcción, presentaciones, renders, otros)
// — todo en una sola pestaña "Archivos".
export const ARTIFACT_TYPES = [
  // Prepress
  { key: "input", code: "IN", label: "Input del cliente (arte/imágenes/fuentes)", group: "Prepress" },
  { key: "dieline", code: "DIE", label: "Dieline estructural (1-up)", group: "Prepress" },
  { key: "art1up", code: "ART", label: "Arte 1-up print-ready", group: "Prepress" },
  { key: "imposition", code: "IMP", label: "Imposición / step-&-repeat", group: "Prepress" },
  { key: "proof", code: "PRF", label: "Proof / Mockup", group: "Prepress" },
  { key: "production", code: "PRD", label: "Salida de producción", group: "Prepress" },
  { key: "package", code: "PKG", label: "Fuentes / links empaquetados", group: "Prepress" },
  // Documentos de apoyo
  { key: "manual", code: "MAN", label: "Manual de construcción", group: "Documentos" },
  { key: "presentation", code: "PRE", label: "Presentación", group: "Documentos" },
  { key: "render", code: "RND", label: "Render / 3D", group: "Documentos" },
  { key: "document", code: "DOC", label: "Otro documento", group: "Documentos" },
];

/** Orden de los grupos de la taxonomía. */
export const ARTIFACT_GROUPS = ["Prepress", "Documentos"];

export function artifactType(key) {
  return ARTIFACT_TYPES.find((t) => t.key === key) || null;
}

/**
 * Nombre estandarizado del archivo (estilo SmartName). PURO (testeable).
 * `seq` = orden del artefacto DENTRO de su tipo (1, 2, 3…). El primero no lleva sufijo;
 * del segundo en adelante se agrega "-NN" para que NO se repita el nombre cuando hay
 * varios archivos del mismo tipo (antes dos dielines v1 quedaban con el mismo nombre).
 * Ej: deriveStandardName({id:'J-1042'}, 'dieline', 2, 'pdf')        -> "J-1042-DIE_v02.pdf"
 *     deriveStandardName({id:'J-1042'}, 'dieline', 1, 'pdf', 3)     -> "J-1042-DIE-03_v01.pdf"
 */
export function deriveStandardName(job, typeKey, version, ext, seq) {
  const base = job?.id || (job?.jobNumber ? `J-${job.jobNumber}` : "JOB");
  const code = artifactType(typeKey)?.code || "DOC";
  const v = String(version || 1).padStart(2, "0");
  const e = (ext || "").replace(/^\./, "").toLowerCase() || "dat";
  const slot = Number(seq) >= 2 ? `-${String(seq).padStart(2, "0")}` : "";
  return `${base}-${code}${slot}_v${v}.${e}`;
}

/** Extrae la extensión de un nombre de archivo. PURO. */
export function extOf(filename) {
  const m = /\.([a-z0-9]+)$/i.exec(filename || "");
  return m ? m[1].toLowerCase() : "";
}

/** Crea un artefacto nuevo (sin versiones todavía). Devuelve su id.
 *  `seq` = orden dentro de su tipo (lo calcula el caller: nº de artefactos de ese tipo + 1)
 *  para que el nombre estándar no se repita cuando hay varios del mismo tipo. */
export async function createArtifact(db, jobId, { type, title, seq, user }) {
  const ref = await addDoc(collection(db, `jobs/${jobId}/artifacts`), {
    type,
    title: title || artifactType(type)?.label || type,
    seq: Number(seq) || 1,
    currentVersion: 0,
    latestUrl: "",
    latestName: "",
    approval: { status: "pending", by: null, at: null, reason: "" },
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
  });
  return ref.id;
}

/**
 * Agrega una versión nueva a un artefacto (append-only) y la deja como vigente.
 * Subir una versión nueva RESETEA la aprobación a 'pending' (hay que re-aprobar).
 */
export async function addArtifactVersion(db, jobId, artifactId, version) {
  const nextN = (version.previousVersion || 0) + 1;
  await addDoc(collection(db, `jobs/${jobId}/artifacts/${artifactId}/versions`), {
    version: nextN,
    url: version.url,
    fileName: version.fileName,
    originalName: version.originalName || "",
    size: version.size || 0,
    contentType: version.contentType || "",
    note: version.note || "",
    uploadedBy: version.user?.uid || null,
    uploadedByName: version.user?.displayName || version.user?.email || "sistema",
    uploadedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, `jobs/${jobId}/artifacts/${artifactId}`), {
    currentVersion: nextN,
    latestUrl: version.url,
    latestName: version.fileName,
    latestOriginalName: version.originalName || "",
    approval: { status: "pending", by: null, at: null, reason: "" },
  });
  return nextN;
}

/**
 * Cambia el TIPO (categoría) de un artefacto ya creado. Actualiza el título y, si se
 * pasa `latestName`, también el nombre estándar visible (para que el código de tipo
 * coincida con la nueva categoría). El archivo en sí (URL) no se re-sube.
 */
export async function setArtifactType(db, jobId, artifactId, { type, latestName }) {
  const patch = { type, title: artifactType(type)?.label || type };
  if (latestName) patch.latestName = latestName;
  await updateDoc(doc(db, `jobs/${jobId}/artifacts/${artifactId}`), patch);
}

/**
 * Borra un artefacto (el documento de la ranura). Nota: las versiones quedan en la
 * subcolección y el binario en Storage; para el MVP basta con quitar el artefacto del
 * listado. (Borrado físico de versiones/Storage se puede añadir luego con una function.)
 */
export async function deleteArtifact(db, jobId, artifactId) {
  await deleteDoc(doc(db, `jobs/${jobId}/artifacts/${artifactId}`));
}

/**
 * Corrige nombres DUPLICADOS de artefactos: agrupa por tipo, ordena por creación, asigna
 * `seq` (1,2,3…) y re-deriva `latestName` para que cada uno sea único. Solo escribe los
 * que cambian. Devuelve cuántos corrigió. (Usar una vez para sanear datos viejos.)
 */
export async function fixArtifactNames(db, jobId, job, artifacts) {
  const byType = {};
  (artifacts || []).forEach((a) => { (byType[a.type] = byType[a.type] || []).push(a); });
  let changed = 0;
  for (const type in byType) {
    const list = byType[type].slice().sort(
      (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
    );
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      const seq = i + 1;
      const ext = extOf(a.latestName || "") || "dat";
      const expected = deriveStandardName(job, type, a.currentVersion || 1, ext, seq);
      if (a.latestName !== expected || a.seq !== seq) {
        await updateDoc(doc(db, `jobs/${jobId}/artifacts/${a.id}`), { seq, latestName: expected });
        changed++;
      }
    }
  }
  return changed;
}

/** Fija la decisión de aprobación con traza (quién/cuándo/motivo). */
export async function setApproval(db, jobId, artifactId, { status, user, reason }) {
  await updateDoc(doc(db, `jobs/${jobId}/artifacts/${artifactId}`), {
    approval: {
      status, // 'approved' | 'rejected' | 'pending'
      by: user?.displayName || user?.email || user?.uid || "sistema",
      at: serverTimestamp(),
      reason: reason || "",
    },
  });
}
